/* eslint-disable lingui/no-unlocalized-strings -- caminhos HTTP, valores técnicos do fio e rótulos de log, não texto de UI */
import { z } from 'zod';

import { getZetraMargin, type ZetraMargin } from '@/modules/simulation/public';
import type { Cents } from '@/shared/format/money';
import { stripDocumentSeparators } from '@/shared/format/document';
import { ApiError, creditApi } from '@/shared/http';
import { wire, withSignal } from '@/shared/reference/wire';

import type { CreditNote } from './credit-note.wire';
import type { WarrantyItem } from './screens/tab-form';

// Consulta de margem do detalhe da operação (unidade 9A): "Consultar Crédito Trabalhador" (Dataprev,
// MarginQueryFormContainer.tsx com `isDataprevEmployee`), "Consultar › Margem" do Zetra (o mesmo container com
// `isZetra`) e "Incluir garantias" (UpdateManualWarrrantyContainer.tsx). Cada chamada repete o legado (U02), com
// arquivo:linha; a margem Zetra guardada vem pela porta da Simulação. Nenhuma função aqui grava na operação: a
// tela escreve a garantia no formulário (`setValue`, SelectWarrantyContainer.tsx) e só o Salvar ou o "Incluir
// garantias" enviam.

export { getZetraMargin, type ZetraMargin };

const text = z.string().nullish();
const num = z.number().nullish();
const textOrNumber = z.union([z.string(), z.number()]).nullish();

// ─── Dataprev, crédito do trabalhador (`IDataprevMarginQueryReadModel`, ZetraMarginQueryReadModel.ts:7, generics.ts:72-110) ─

const dataprevEmployeeSchema = z.looseObject({
  cpf: textOrNumber,
  matricula: text,
  nome: text,
  nomeEmpregador: text,
  numeroInscricaoEmpregador: textOrNumber,
  /** Reais (`Math.round(valorMargemDisponivel * 100)`, SelectWarrantyContainer.tsx:165). */
  valorMargemDisponivel: num,
  /** `DDMMAAAA` (`convertToDateWithMoment`, DateFormat.ts:269-284). */
  dataAdmissao: text,
  CompetenciaAtual: textOrNumber,
  competenciaAtual: textOrNumber,
  DataPrimeiroRepasse: text,
  dataPrimeiroRepasse: text,
  sexo: z.looseObject({ descricao: text }).nullish(),
});
export type DataprevEmployee = z.infer<typeof dataprevEmployeeSchema>;

export const dataprevMarginSchema = z.looseObject({
  createdAt: text,
  result: z.array(dataprevEmployeeSchema).nullish(),
});
export type DataprevMargin = z.infer<typeof dataprevMarginSchema>;
const dataprevMarginListSchema = z.array(dataprevMarginSchema);

/** `IDataPrevCacheAuthorizationResponse` (ZetraMarginQueryReadModel.ts:18-23). */
const cacheAuthorizationSchema = z.looseObject({ dataValidadeAutorizacao: text, tokenAutorizacao: text, tokenValido: z.boolean().nullish() });
export type CacheAuthorization = z.infer<typeof cacheAuthorizationSchema>;

export interface MarginQueryParams {
  readonly registrationNumber: string;
  readonly personId: string | null | undefined;
  readonly creditProductId: string | null | undefined;
}

/** Os parâmetros na ordem do legado; `undefined` sai da query (axios), `null` também. */
function marginParams(input: MarginQueryParams): Record<string, string> {
  const params: Record<string, string> = { registrationNumber: input.registrationNumber };
  if (input.personId) params['personId'] = input.personId;
  if (input.creditProductId) params['creditProductId'] = input.creditProductId;
  return params;
}

/**
 * A margem guardada (`getDataprevMarginQueryAsync`, DataprevEmployee.ts:39-43; `useDataprevMarginQuery`,
 * dataprev/index.ts:75-120, com `{ registrationNumber, personId, creditProductId: productId }`). Só leitura.
 */
export async function getDataprevMargin(input: MarginQueryParams, signal?: AbortSignal): Promise<DataprevMargin[]> {
  const data = await creditApi.get<unknown>('/DataprevEmployee/FreeMarginQuery', { params: marginParams(input), ...withSignal(signal) });
  return Array.isArray(data) ? wire(dataprevMarginListSchema, data, 'GET /DataprevEmployee/FreeMarginQuery') : [];
}

/**
 * **Consulta nova à Dataprev, paga** (`postUpdateDataprevMarginQueryAsync`, DataprevEmployee.ts:27-37; "Obter
 * margem"/"Atualizar margem", MarginQueryFormContainer.tsx:770-777): corpo `{}` e os três na query. Prévia e clique (X01).
 */
export async function requestDataprevMargin(input: MarginQueryParams): Promise<unknown> {
  return creditApi.post('/DataprevEmployee/FreeMarginQuery', {}, { params: marginParams(input) });
}

/**
 * A autorização do titular em cache (`postDataprevCacheAuthorizationAsync`, DataprevEmployee.ts:54-67): só o
 * `registrationNumber` na query e corpo `{}`. Erro = ainda não autorizou (MarginQueryFormContainer.tsx:264-276).
 */
export async function checkDataprevAuthorization(registrationNumber: string, signal?: AbortSignal): Promise<CacheAuthorization> {
  const data = await creditApi.post('/DataprevEmployee/CacheAuthorizationToken', {}, { params: { registrationNumber }, ...withSignal(signal) });
  return wire(cacheAuthorizationSchema, data ?? {}, 'POST /DataprevEmployee/CacheAuthorizationToken');
}

/** "Whatsapp Reativo", "SMS Ativo", "Link Web" (DataPrevAuthorizationContainer.tsx:51-61, `CHANNEL_MAP` do useAuthorizationLinkRetriever.ts:38-42). */
export const AUTHORIZATION_CHANNELS = ['WhatsappInbound', 'SMS', 'WebLink'] as const;
export type AuthorizationChannel = (typeof AUTHORIZATION_CHANNELS)[number];

export interface AuthorizationRequestBody {
  registrationNumber: string;
  phoneNumber: string | undefined;
  productId: string | undefined;
  authorizationLink: '';
  channel: AuthorizationChannel;
  additionalData: { ip: 'META'; geoLocation: 'META'; deviceModel: 'META'; whatsAppValidation: true };
  UY3Origin: true;
}

/** O corpo do pedido de autorização (useAuthorizationLinkRetriever.ts:52-68), sem os campos do INSS. */
export function authorizationRequestBody(input: { readonly registrationNumber: string; readonly phoneNumber: string | null | undefined; readonly productId: string | null | undefined; readonly channel: AuthorizationChannel }): AuthorizationRequestBody {
  return {
    registrationNumber: input.registrationNumber,
    // `values?.phoneNumber?.replace(/\D/g, '')`: telefone não é documento, só dígitos; `null` (nunca digitado) sai do JSON.
    phoneNumber: input.phoneNumber === null || input.phoneNumber === undefined ? undefined : input.phoneNumber.replace(/\D/g, ''),
    productId: input.productId ?? undefined,
    authorizationLink: '',
    channel: input.channel,
    additionalData: { ip: 'META', geoLocation: 'META', deviceModel: 'META', whatsAppValidation: true },
    UY3Origin: true,
  };
}

/** Envia o pedido de autorização ao titular (`postDataprevAuthorizationMarginAsync`, DataprevEmployee.ts:68-72). */
export async function requestDataprevAuthorization(body: AuthorizationRequestBody): Promise<unknown> {
  return creditApi.post('/DataprevEmployee/AuthorizationMargin', body);
}

/**
 * As autorizações do CPF (`getDataprevAuthorizationAsync`, DataprevEmployee.ts:45-52, pelo `useDataprevAuthorizationList`
 * com `{ registrationNumber }`). O legado não falha aqui: o `refetch` devolve o erro sem lançar.
 */
export async function listDataprevAuthorizations(registrationNumber: string): Promise<unknown> {
  try {
    return await creditApi.get('/DataprevEmployee/AuthorizationMargin', { params: { registrationNumber } });
  } catch {
    return undefined;
  }
}

const recordOf = (value: unknown): Record<string, unknown> | undefined => (typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : undefined);
const stringOf = (value: unknown): string => (typeof value === 'string' ? value : '');

/** O link de autorização: o da resposta, senão o da lista (o primeiro com link, depois o primeiro), senão o do objeto (useAuthorizationLinkRetriever.ts:77-101). */
export function authorizationLinks(response: unknown, list: unknown): { readonly link: string; readonly smsLink: string } {
  const created = recordOf(response);
  let link = stringOf(created?.['authorizationLink']);
  const data = recordOf(list)?.['data'];
  if (!link && Array.isArray(data) && data.length > 0) {
    const items = data.map(recordOf);
    link = stringOf(items.find((item) => stringOf(item?.['authorizationLink']))?.['authorizationLink']) || stringOf(items[0]?.['authorizationLink']);
  }
  if (!link) link = stringOf(recordOf(data)?.['authorizationLink']);
  return { link, smsLink: stringOf(created?.['authorizationSmsLink']) };
}

/** 403 com `MISSING_RESOURCE_PERMISSION`: o legado não abre a autorização (MarginQueryFormContainer.tsx:266-272, 388-392). */
export function isMissingPermission(error: unknown): boolean {
  const api = ApiError.from(error);
  return api.status === 403 && api.code === 'MISSING_RESOURCE_PERMISSION';
}

export interface DataprevRestriction {
  readonly messageError: string;
}

/**
 * As restrições do `GET` da margem que falhou (MarginQueryFormContainer.tsx:602-618): o `reasonForIneligibility`
 * de cada resposta ou a `messageError` dela, sem vazias e sem repetidas; a mensagem do erro fica de reserva.
 */
export function dataprevRestrictions(error: unknown): { readonly items: readonly DataprevRestriction[]; readonly message: string | undefined } {
  const payload = ApiError.from(error).payload as Record<string, unknown>;
  const details = recordOf(payload['details']);
  const responses = details?.['dataprevValidationResponses'];
  const flat = (Array.isArray(responses) ? responses : []).flatMap((response) => {
    const item = recordOf(response);
    const reasons = item?.['reasonForIneligibility'];
    if (Array.isArray(reasons) && reasons.length > 0) return reasons.map((reason) => stringOf(recordOf(reason)?.['messageError']));
    return [stringOf(item?.['messageError'])];
  });
  const seen = new Set<string>();
  const items = flat.filter((message) => message.trim() !== '' && !seen.has(message) && Boolean(seen.add(message))).map((messageError) => ({ messageError }));
  const message = payload['message'];
  return { items, message: typeof message === 'string' ? message : undefined };
}

// ─── Zetra (`postUpdateZetraMarginQueryAsync`, zetra.ts:56-63) ─────────────────────────────────────

/**
 * **Consulta nova ao Zetra, paga** ("Obter margem" com tomador, MarginQueryFormContainer.tsx:780-784): só
 * `personId` e `creditProductId` na query, e a lista de tokens (vazia) no corpo. Prévia e clique (X01).
 */
export async function requestZetraMargin(input: { readonly personId: string | null | undefined; readonly creditProductId: string | null | undefined }): Promise<unknown> {
  // `undefined` e `null` saem da query (axios), na ordem do legado (zetra.ts:57-58); a aba Crédito da Operação não tem `personId`.
  const params: Record<string, string> = {};
  if (input.personId) params['personId'] = input.personId;
  if (input.creditProductId) params['creditProductId'] = input.creditProductId;
  return creditApi.post('/Zetra/FreeMarginQuery', [], { params });
}

/** Um token do servidor no corpo da atualização da Zetra (`TUpdateZetraProps.tokens`, ZetraMarginQueryUpdateModel.ts:10-20). */
export interface ZetraUpdateToken {
  readonly warrantyRegistrationOffice: string;
  readonly token: { readonly tokenType: string; readonly tokenValue: string };
  readonly expirationDate: string;
}

/**
 * **Consulta nova à Zetra, paga, com os tokens** (`postUpdateZetraMarginQueryAsync`, zetra.ts:56-63, pelo `UpdateZetra`,
 * MarginQueryFormContainer.tsx:708-718 e MarginQueryToNewSimulation.tsx:107-115): os tokens do servidor no corpo e, na
 * query, `registrationNumber`, `personId`, `creditProductId` e `employeeCode` como o formulário mandar. O CPF vai
 * **formatado** (o campo do `UpdateZetra` nasce com `formatDocumentNumber`, UpdateZetraSchema.ts:14-18); `undefined`
 * sai da query e `null` vira vazio (o `Qs` do legado). Sem `tenant`, timeout padrão. Veio da Simulação (R4) na R5.
 */
export async function requestZetraMarginUpdate(input: {
  readonly registrationNumber?: string | null | undefined;
  readonly personId?: string | null | undefined;
  readonly creditProductId?: string | null | undefined;
  readonly employeeCode?: string | null | undefined;
  readonly tokens?: readonly ZetraUpdateToken[];
}): Promise<unknown> {
  const params = { registrationNumber: input.registrationNumber, personId: input.personId, creditProductId: input.creditProductId, employeeCode: input.employeeCode };
  return creditApi.post('/Zetra/FreeMarginQuery', input.tokens ?? [], { params });
}

// ─── Incluir garantias (`putUpdateRevisionWarranty`, creditNote.ts:675-683) ─────────────────────────

/** `PUT /CreditNote/{id}/warranty` com `{ productId, warranty }`: a lista do formulário como está (UpdateManualWarrrantyContainer.tsx:26-32). */
export async function updateRevisionWarranty(id: string, body: { readonly productId: string | null | undefined; readonly warranty: readonly WarrantyItem[] }): Promise<unknown> {
  return creditApi.put(`/CreditNote/${id}/warranty`, body);
}

// ─── regras puras ─────────────────────────────────────────────────────────────────────────────────

/**
 * O CPF da consulta (MarginQueryFormContainer.tsx:121-123, 152): o da operação ou o do tomador, só sem os
 * separadores (o legado usava `onlyNumbers`; aqui não se apagam letras, regra da casa).
 */
export function marginDocument(note: Pick<CreditNote, 'registrationNumber' | 'personRegistrationNumber'>): string {
  return stripDocumentSeparators(note.registrationNumber || note.personRegistrationNumber || '');
}

/** O valor da escolha de cada cartão do Dataprev (Summary/Zetra.tsx:27; SelectWarrantyContainer.tsx:147). */
export const dataprevKey = (item: DataprevEmployee) => `${String(item.numeroInscricaoEmpregador ?? '')}-${item.matricula ?? ''}-${String(item.valorMargemDisponivel ?? '')}`;

/** Reais → centavos, como o `Math.round(x * 100)` do legado. */
export const reaisToCents = (value: number | null | undefined): Cents => Math.round((value ?? 0) * 100);

/** `convertToDateWithMoment` (DateFormat.ts:269-284): `DDMMAAAA` → meia-noite em -03:00; outro formato, `null`. */
export function admissionDate(value: string | null | undefined): string | null {
  if (typeof value !== 'string' || !/^\d{8}$/.test(value)) return null;
  const [day, month, year] = [value.slice(0, 2), value.slice(2, 4), value.slice(4, 8)];
  const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
  if (date.getUTCDate() !== Number(day) || date.getUTCMonth() !== Number(month) - 1) return null;
  return `${year}-${month}-${day}T00:00:00-03:00`;
}

/** A competência como o legado a grava na garantia (SelectWarrantyContainer.tsx:163-164). */
function discountStartPeriod(item: DataprevEmployee): string | undefined {
  const value = item.CompetenciaAtual ?? item.competenciaAtual;
  return value === null || value === undefined ? undefined : String(value);
}

/** A garantia "Crédito do trabalhador" de uma margem escolhida (SelectWarrantyContainer.tsx:154-167). */
export function dataprevWarranty(item: DataprevEmployee): WarrantyItem {
  return {
    warrantyOption: 'FreeMargin',
    warrantyType: 'DataprevEmployee',
    dataprev_EmployerName: item.nomeEmpregador,
    dataprev_EmployeeCode: item.matricula,
    dataprev_EmployerRegistrationCode: 1,
    dataprev_EmployeeAdmissionDate: admissionDate(item.dataAdmissao),
    dataprev_EmployerRegistrationNumber: item.numeroInscricaoEmpregador,
    dataprev_DiscountStartPeriod: discountStartPeriod(item),
    totalValue: reaisToCents(item.valorMargemDisponivel),
    dataprev_DataPrimeiroRepasse: item.DataPrimeiroRepasse ?? item.dataPrimeiroRepasse,
  };
}

/** O que "Confirmar" do Dataprev escreve no formulário (SelectWarrantyContainer.tsx:142-192). */
export interface MarginWrite {
  readonly warranty: WarrantyItem[];
  readonly requestedAmount: Cents;
  readonly firstPaymentDate?: string;
}

/** A escolha do Dataprev substitui as garantias; a margem da primeira vira o valor e a reserva (pela parcela). */
export function dataprevWrite(selected: readonly DataprevEmployee[]): MarginWrite | null {
  const first = selected[0];
  if (!first) return null;
  const firstPaymentDate = first.DataPrimeiroRepasse ?? first.dataPrimeiroRepasse ?? undefined;
  return { warranty: selected.map(dataprevWarranty), requestedAmount: reaisToCents(first.valorMargemDisponivel), ...(firstPaymentDate ? { firstPaymentDate } : {}) };
}

type ZetraServer = NonNullable<NonNullable<ZetraMargin['result']>['servidores']>[number];

/** O valor da escolha de cada margem Zetra (Summary/Zetra.tsx:85; SelectWarrantyContainer.tsx:243-245). */
export const zetraKey = (server: ZetraServer, margin: NonNullable<ZetraServer['margens']>[number]) => `${margin.codigo ?? ''}-${server.orgaoCodigo ?? ''}-${String(margin.valorDisponivel ?? '')}`;

export interface ZetraWarrantyValues {
  readonly warrantyOption: string;
  /** O instante do `warrantyDeadline` (o `date()` do yup vira ISO em UTC no JSON). */
  readonly warrantyDeadline: string;
  readonly previousAdeNumber: string | null;
}

/**
 * A escolha do Zetra (SelectWarrantyContainer.tsx:234-273, `mapZetraWarrantyPublicPayroll`, helpers:52-76): os
 * vínculos com alguma margem marcada viram garantias de consignado público, **somadas às atuais**; a soma das margens
 * do primeiro vira o valor e a reserva (pela parcela).
 */
export function zetraWrite(margin: ZetraMargin | undefined, keys: readonly string[], values: ZetraWarrantyValues, registrationNumber: string, current: readonly WarrantyItem[]): MarginWrite {
  const servers = (margin?.result?.servidores ?? []).filter((server) => (server.margens ?? []).some((item) => keys.includes(zetraKey(server, item))));
  const mapped: WarrantyItem[] = servers.map((server) => ({
    warrantyType: 'PublicPayroll',
    employeeCode: server.matricula,
    governmentDepartmentCode: server.orgaoCodigo,
    paymentAmountInCents: reaisToCents((server.margens ?? []).reduce((sum, item) => sum + (item.valorDisponivel ?? 0), 0)),
    codOrgao: server.orgaoCodigo,
    warrantyOption: values.warrantyOption,
    warrantyDeadline: values.warrantyDeadline,
    previousAdeNumber: values.previousAdeNumber,
    simulationRegistrationNumber: registrationNumber,
  }));
  const first = mapped[0]?.['paymentAmountInCents'];
  return { warranty: [...current, ...mapped], requestedAmount: typeof first === 'number' && first !== 0 ? first : 0 };
}
